// Commitment create/edit form — FE-043 + FE-043b parity v2.
//
// Mirrors the Pencil ``Create Commitment`` frame:
// - Two-column rows for the header fields (Title|Account,
//   Assignee|Complexity, Description full-width, Due|Reviewer,
//   Watchers|Document, Tags|Depends-on, Category|Priority).
// - Complexity renders as S/M/L/XL pill buttons instead of a
//   plain dropdown.
// - Visibility dropdown (Team-visible / Private / Employer-visible —
//   the last is disabled "Coming soon"). Selecting Private snaps
//   the assignee to the current user and locks the field.
// - Footer hosts a centered "Create Commitment" CTA + a small
//   Cancel link.
//
// Watchers / Document / Depends-on are visual placeholders today —
// the BE accepts the underlying data but FE pickers land in a
// later chunk. Each placeholder carries an honest hint.

import { useContext } from "react";
import { Link } from "react-router-dom";

import { BlockNoteEditor } from "@/components/blocknote/BlockNoteEditor";
import { AuthContext } from "@/features/auth/AuthContext";

import {
  COMMITMENT_VISIBILITY_OPTIONS,
  resolveAssigneeForVisibility,
  shouldClearReviewerForVisibility,
} from "./commitmentVisibility";
import { useCategoriesPickerQuery } from "./hooks";
import type { CommitmentFormErrors } from "./commitmentFormValidation";
import type {
  CommitmentFormMode,
  CommitmentFormValues,
  CommitmentFormVisibility,
} from "./types";


export interface CommitmentFormProps {
  mode: CommitmentFormMode;
  values: CommitmentFormValues;
  errors: CommitmentFormErrors;
  onChange: <K extends keyof CommitmentFormValues>(
    key: K, value: CommitmentFormValues[K],
  ) => void;
  onSubmit: () => void;
  isPending: boolean;
  mutationError: Error | null;
  cancelHref: string;
}


const COMPLEXITY_PILLS: Array<{ value: string; label: string }> = [
  { value: "1", label: "S" },
  { value: "2", label: "M" },
  { value: "3", label: "L" },
  { value: "5", label: "XL" },
];


export function CommitmentForm({
  mode, values, errors, onChange, onSubmit,
  isPending, mutationError, cancelHref,
}: CommitmentFormProps) {
  const categoriesQuery = useCategoriesPickerQuery();
  const auth = useContext(AuthContext);
  const currentUserId = auth?.user?.id ?? null;
  const submitLabel = mode === "create"
    ? "Create Commitment"
    : "Save changes";
  const isPrivate = values.visibility === "private";

  function handleVisibilityChange(next: CommitmentFormVisibility): void {
    onChange("visibility", next);
    const assigneePatch = resolveAssigneeForVisibility({
      nextVisibility: next,
      currentAssignedTo: values.assigned_to,
      currentUserId,
    });
    if (assigneePatch) {
      onChange("assigned_to", assigneePatch.assigned_to);
      onChange("assigned_job_title", assigneePatch.assigned_job_title);
      onChange(
        "assigned_client_contact_id",
        assigneePatch.assigned_client_contact_id,
      );
    }
    if (shouldClearReviewerForVisibility(next) && values.reviewer_id) {
      onChange("reviewer_id", "");
    }
  }

  return (
    <form
      data-testid="commitment-form"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className="flex flex-col gap-5 rounded-lg border border-border-subtle bg-raised p-6"
    >
      {/* Row 1 — Title | Account */}
      <div className="grid gap-5 md:grid-cols-2">
        <Field
          label="Title"
          name="title"
          required
          error={errors.title}
        >
          <input
            type="text"
            data-testid="commitment-form-title"
            value={values.title}
            onChange={(e) => onChange("title", e.target.value)}
            placeholder="Enter title"
            className="rounded-md border border-border-subtle bg-canvas/40 px-3 py-2 text-sm text-text-primary"
            autoComplete="off"
          />
        </Field>
        <Field
          label="Account"
          name="account_id"
          error={errors.account_id}
          hint="Account UUID. Account picker lands with FE-051."
        >
          <input
            type="text"
            data-testid="commitment-form-account-id"
            value={values.account_id}
            onChange={(e) => onChange("account_id", e.target.value)}
            placeholder="Select Account"
            className="rounded-md border border-border-subtle bg-canvas/40 px-3 py-2 text-sm text-text-primary"
            autoComplete="off"
          />
        </Field>
      </div>

      {/* Row 2 — Assignee | Complexity (S/M/L/XL pills) */}
      <div className="grid gap-5 md:grid-cols-2">
        <Field
          label="Assignee"
          name="assigned_to"
          error={errors.assigned_to}
          hint={
            isPrivate
              ? "Locked while Private — only the current user can be assigned."
              : "User UUID. Member picker lands with FE-051."
          }
        >
          <input
            type="text"
            data-testid="commitment-form-assigned-to"
            value={values.assigned_to}
            onChange={(e) => onChange("assigned_to", e.target.value)}
            placeholder="Select Assignee"
            disabled={isPrivate}
            aria-disabled={isPrivate}
            className="rounded-md border border-border-subtle bg-canvas/40 px-3 py-2 text-sm text-text-primary disabled:cursor-not-allowed disabled:bg-recessed disabled:text-text-tertiary"
          />
        </Field>
        <Field
          label="Complexity"
          name="complexity"
          error={errors.complexity}
          hint="T-shirt size on the Fibonacci point scale."
        >
          <div
            role="radiogroup"
            aria-label="Complexity"
            data-testid="commitment-form-complexity-pills"
            className="flex flex-wrap items-center gap-2 pt-1"
          >
            {COMPLEXITY_PILLS.map((p) => {
              const active = values.complexity === p.value;
              return (
                <button
                  key={p.value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  data-testid={`commitment-form-complexity-${p.value}`}
                  onClick={() =>
                    onChange("complexity", active ? "" : p.value)
                  }
                  className={[
                    "inline-flex h-8 min-w-[32px] items-center justify-center rounded-full px-2 text-xs font-semibold transition-fast ease-out",
                    active
                      ? "bg-teal text-text-inverse"
                      : "border border-border-subtle bg-raised text-text-secondary hover:text-text-primary",
                  ].join(" ")}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
        </Field>
      </div>

      {/* Row 3 — Visibility */}
      <Field
        label="Visibility"
        name="visibility"
        hint={
          isPrivate
            ? "Only you can see this Commitment."
            : "Everyone on your team can see this Commitment."
        }
      >
        <select
          data-testid="commitment-form-visibility"
          value={values.visibility}
          onChange={(e) =>
            handleVisibilityChange(
              e.target.value as CommitmentFormVisibility,
            )
          }
          className="rounded-md border border-border-subtle bg-canvas/40 px-3 py-2 text-sm text-text-primary md:max-w-xs"
        >
          {COMMITMENT_VISIBILITY_OPTIONS.map((opt) => (
            <option
              key={opt.value}
              value={opt.value}
              disabled={opt.disabled}
              data-testid={`commitment-form-visibility-option-${opt.value}`}
            >
              {opt.label}
              {opt.hint ? ` — ${opt.hint}` : ""}
            </option>
          ))}
        </select>
      </Field>

      {/* Row 4 — Description (full-width). Always rich; the BlockNote
          editor lazily creates a document on first edit and the
          captured id rides along on submit. */}
      <Field label="Description" name="description">
        <div data-testid="commitment-form-description">
          <BlockNoteEditor
            documentId={values.description_document_id || null}
            documentKind="commitment_description"
            fallbackText={values.description || null}
            onCreated={(documentId) => {
              onChange("description_document_id", documentId);
            }}
          />
        </div>
      </Field>

      {/* Row 5 — Due | Reviewer */}
      <div className="grid gap-5 md:grid-cols-2">
        <Field label="Due Date" name="due_date_time">
          <input
            type="datetime-local"
            data-testid="commitment-form-due"
            value={values.due_date_time}
            onChange={(e) => onChange("due_date_time", e.target.value)}
            className="rounded-md border border-border-subtle bg-canvas/40 px-3 py-2 text-sm text-text-primary"
          />
        </Field>
        <Field
          label="Reviewer"
          name="reviewer_id"
          hint={
            isPrivate
              ? "Private Commitments cannot have reviewers."
              : "User UUID. Member picker lands with FE-051."
          }
        >
          <input
            type="text"
            data-testid="commitment-form-reviewer"
            value={values.reviewer_id}
            onChange={(e) => onChange("reviewer_id", e.target.value)}
            placeholder="Select Reviewer"
            disabled={isPrivate}
            aria-disabled={isPrivate}
            className="rounded-md border border-border-subtle bg-canvas/40 px-3 py-2 text-sm text-text-primary disabled:cursor-not-allowed disabled:bg-recessed disabled:text-text-tertiary"
          />
        </Field>
      </div>

      {/* Row 6 — Watchers | Document (placeholders) */}
      <div className="grid gap-5 md:grid-cols-2">
        <Field
          label="Watchers"
          name="watchers"
          hint="Watcher picker lands with the comments/activity chunk."
        >
          <input
            type="text"
            data-testid="commitment-form-watchers"
            placeholder="Select Watchers"
            disabled
            className="rounded-md border border-border-subtle bg-canvas/40 px-3 py-2 text-sm text-text-tertiary disabled:cursor-not-allowed"
          />
        </Field>
        <Field
          label="Document"
          name="document"
          hint="File upload lands in a later chunk."
        >
          <div className="flex items-center gap-2 rounded-md border border-border-subtle bg-canvas/40 px-3 py-2">
            <button
              type="button"
              data-testid="commitment-form-document"
              disabled
              className="rounded-md border border-border-subtle bg-raised px-2 py-1 text-xs text-text-secondary disabled:cursor-not-allowed"
            >
              Choose Files
            </button>
            <span className="text-xs text-text-tertiary">No file chosen</span>
          </div>
        </Field>
      </div>

      {/* Row 7 — Tags | Depends on */}
      <div className="grid gap-5 md:grid-cols-2">
        <Field
          label="Tags"
          name="tag_slugs"
          hint="Comma-separated tag slugs (e.g. erisa, hipaa). Regulatory comes from the tag itself."
        >
          <input
            type="text"
            data-testid="commitment-form-tags"
            value={values.tag_slugs}
            onChange={(e) => onChange("tag_slugs", e.target.value)}
            placeholder="Search or select tags…"
            className="rounded-md border border-border-subtle bg-canvas/40 px-3 py-2 text-sm text-text-primary"
          />
        </Field>
        <Field
          label="Depends on"
          name="depends_on"
          hint="Dependency picker lands in a later chunk."
        >
          <input
            type="text"
            data-testid="commitment-form-depends-on"
            placeholder="Select Commitment"
            disabled
            className="rounded-md border border-border-subtle bg-canvas/40 px-3 py-2 text-sm text-text-tertiary disabled:cursor-not-allowed"
          />
        </Field>
      </div>

      {/* Row 8 — Category | Priority */}
      <div className="grid gap-5 md:grid-cols-2">
        <Field
          label="Category"
          name="category_slug"
          error={errors.category_slug}
        >
          <select
            data-testid="commitment-form-category-slug"
            value={values.category_slug}
            onChange={(e) => onChange("category_slug", e.target.value)}
            className="rounded-md border border-border-subtle bg-canvas/40 px-3 py-2 text-sm text-text-primary"
          >
            <option value="">Select Category</option>
            {(categoriesQuery.data?.categories ?? []).map((c) => (
              <option key={c.slug} value={c.slug}>{c.display_name}</option>
            ))}
          </select>
        </Field>
        <Field label="Priority" name="priority">
          <select
            data-testid="commitment-form-priority"
            value={values.priority}
            onChange={(e) =>
              onChange(
                "priority",
                e.target.value as CommitmentFormValues["priority"],
              )
            }
            className="rounded-md border border-border-subtle bg-canvas/40 px-3 py-2 text-sm text-text-primary"
          >
            <option value="">—</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
        </Field>
      </div>

      {mutationError && (
        <div
          role="alert"
          data-testid="commitment-form-error"
          className="rounded-md bg-ember-tint px-3 py-2 text-sm text-ember"
        >
          Couldn't save this Commitment. Refresh and try again, or
          check the network connection.
        </div>
      )}

      <div className="flex flex-col items-center gap-2 border-t border-border-subtle pt-5">
        <button
          type="submit"
          data-testid="commitment-form-submit"
          disabled={isPending}
          className="w-full max-w-md rounded-full bg-teal px-4 py-2.5 text-sm font-semibold text-text-inverse hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isPending ? "Saving…" : submitLabel}
        </button>
        <Link
          to={cancelHref}
          data-testid="commitment-form-cancel"
          className="text-xs text-text-tertiary hover:text-text-primary"
        >
          Cancel
        </Link>
      </div>
    </form>
  );
}


function Field({
  label, name, required, error, hint, children,
}: {
  label: string;
  name: string;
  required?: boolean;
  error?: string | undefined;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label
      htmlFor={`commitment-form-${name}`}
      className="flex flex-col gap-1 text-xs font-medium text-text-secondary"
    >
      <span>
        {label}
        {required && <span className="ml-0.5 text-ember">*</span>}
      </span>
      {children}
      {hint && (
        <span className="text-[11px] font-normal text-text-tertiary">
          {hint}
        </span>
      )}
      {error && (
        <span
          data-testid={`commitment-form-${name}-error`}
          className="text-[11px] font-medium text-ember"
        >
          {error}
        </span>
      )}
    </label>
  );
}
